import React, { useState } from 'react';
import { PropTypes } from 'prop-types';
import {
  StyledIcon,
  StyledButton,
  StyledDiv,
  StyledButtonWrapper,
  NeutralAnchor,
  DEFAULT_THEME,
  DEFAULT_ICON,
  buttonThemeNames,
  iconThemeNames,
  SpinnerWrapper,
  StyledSpinner
} from './PortalButton.styles';
import LocaleLink from '../../../components/LocaleLink';
import { UserCtx } from '../../../contexts/UserCtx';
import { ReactChildrenPropType } from '../../../lib/commonPropTypes';

export function PortalButton(props) {
  const { children, href, target, name, icon, label, route, params, btnTheme, loading, reqPermission, tooltip } = props;
  const [showTooltip, setShowTooltip] = useState(false);
  let buttonContent;

  const passThroughProps = { ...props };

  // If a permission is required, check the user has it
  if (reqPermission !== undefined) {
    const userCtx = React.useContext(UserCtx);
    const denyReason = userCtx.denyReason(reqPermission);
    if (denyReason !== undefined) {
      passThroughProps.title = denyReason;
      passThroughProps.disabled = true;
    }
  }

  if (icon !== '') {
    buttonContent = <StyledIcon className="icon" selectedIcon={icon} />;
  } else if (label) {
    buttonContent = <div>{label}</div>;
  } else if (children) {
    buttonContent = children;
  }

  const wrapSpinner = (
    <SpinnerWrapper>
      <StyledSpinner size={16} thickness={8} />
    </SpinnerWrapper>
  );

  if (loading)
    buttonContent = (
      <>
        {buttonContent}
        {wrapSpinner}
      </>
    );

  const button = tooltip ? (
    <StyledButtonWrapper>
      <StyledButton
        aria-label={name}
        {...passThroughProps}
        btnTheme={btnTheme || DEFAULT_THEME}
        onClick={(e) => {
          setShowTooltip(false);
          passThroughProps.onClick?.(e);
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {buttonContent}
      </StyledButton>
      {tooltip && showTooltip && <StyledDiv data-tooltip>{tooltip}</StyledDiv>}
    </StyledButtonWrapper>
  ) : (
    <StyledButton aria-label={name} {...passThroughProps} btnTheme={btnTheme || DEFAULT_THEME}>
      {buttonContent}
    </StyledButton>
  );

  /** If we're passed an href prop, render it with an anchor tag */
  if (href) {
    return (
      <NeutralAnchor href={href} target={target}>
        {button}
      </NeutralAnchor>
    );
  }

  if (route) {
    return (
      <LocaleLink route={route} params={params}>
        {button}
      </LocaleLink>
    );
  }
  return button;
}

PortalButton.propTypes = {
  /**
   * className binding for styled-components
   */
  className: PropTypes.string,

  /**
   * Boolean prop to toggle the disabled button state.
   */
  disabled: PropTypes.bool,

  /**
   * If a 'href' tag is provided, the button will render wrapped in an anchor element with styling intact
   */
  href: PropTypes.string,

  /**
   * HTML Name attribute
   * */
  name: PropTypes.string,

  /**
   * onClick callback function
   */
  onClick: PropTypes.func,

  /**
   * Provide a target for your href tag
   */
  target: PropTypes.oneOf(['_blank', '', null]),

  /**
   * Pass in a theme name corresponding to a theme inside Button.theme
   */
  btnTheme: PropTypes.oneOf(buttonThemeNames),

  /**
   * Pass in a theme name corresponding to a theme inside Button.theme
   */
  iconTheme: PropTypes.oneOf(iconThemeNames),

  /**
   * Defines HTML button type attribute
   */
  type: PropTypes.oneOf(['button', 'submit', 'reset', null]),

  /**
   * Uppercase boolean
   */
  uppercase: PropTypes.bool,

  /**
   * optional icon for the button
   */
  icon: PropTypes.string,

  loading: PropTypes.bool,

  /**
   * label
   */
  label: PropTypes.string,

  fullWidth: PropTypes.bool,

  route: PropTypes.string,
  params: PropTypes.shape({}),
  children: ReactChildrenPropType,

  /**
   * Permission required to activate the button (otherwise disabled)
   * If undefined, no permission is required
   */
  reqPermission: PropTypes.string,

  /**
   * Optional tooltip text displayed below the button.
   */
  tooltip: PropTypes.string,

  'data-testid': PropTypes.string
};

PortalButton.defaultProps = {
  className: null,
  disabled: false,
  href: null,
  name: null,
  onClick: () => {},
  target: null,
  btnTheme: DEFAULT_THEME,
  iconTheme: DEFAULT_ICON,
  type: 'button',
  uppercase: false,
  icon: '',
  label: '',
  fullWidth: false,
  route: null,
  params: null,
  children: null,
  loading: false,
  reqPermission: undefined,
  tooltip: null,
  'data-testid': ''
};
